import React, { ReactElement, useEffect, useMemo, useRef } from 'react'
import Highcharts from 'highcharts2/highstock'
import styled, { useTheme } from 'styled-components'
import { ChartContainer } from '../chart-components-style'
import { getOptionsFromTemporalWidget } from './temporal-widget-dom-utils'
import { useStaticDataWidget } from '../../context/StaticDataContext'

require('highcharts/modules/xrange')(Highcharts)

interface TimelineChartProps {
    width: number
    height: number
    updateStatePadding: () => void
}

export const TimelineChart = ({ width, height, updateStatePadding }: TimelineChartProps): ReactElement => {
    const { widgetData, widgetTimeSpan } = useStaticDataWidget()

    // Chart States
    const chart = useRef<Highcharts.Chart>(null)
    const higchartsDOMEl = useRef<HTMLDivElement>(null)
    const theme = useTheme()

    const options = useMemo(() => {
        return getOptionsFromTemporalWidget(widgetTimeSpan, widgetData.interventionSeries, theme)
    }, [theme, widgetData.interventionSeries, widgetTimeSpan])

    useEffect(() => {
        chart.current = Highcharts.chart(higchartsDOMEl.current, options)
        chart.current.showLoading()
        updateStatePadding()

        return () => {
            // cleanup function
            chart.current.destroy()
            chart.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (chart.current) {
            chart.current.update(options)
            updateStatePadding()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, options])

    useEffect(() => {
        chart.current.reflow()
        updateStatePadding()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, width, height])

    return (
        <ChartContainer>
            <Chart ref={higchartsDOMEl} />
        </ChartContainer>
    )
}

const Chart = styled.div`
    height: 100%;
    width: 100%;
    max-height: 30px;
    min-height: 20px;

    .highcharts-container {
        max-height: 30px;
        min-height: 20px;
        .highcharts-root {
            max-height: 30px;
            min-height: 20px;
            .highcharts-axis-labels.highcharts-xaxis-labels text {
                color: var(--gray-900) !important;
                fill: var(--gray-900) !important;
            }
            .highcharts-axis.highcharts-xaxis path {
                stroke: var(--gray-500);
            }
            .highcharts-series-group {
                max-height: 30px;
                min-height: 20px;
                .highcharts-series highcharts-series-0 highcharts-xrange-series highcharts-tracker {
                    max-height: 30px;
                    min-height: 20px;
                    .highcharts-point {
                        max-height: 30px;
                        min-height: 20px;
                        .highcharts-point highcharts-partfill-original {
                            max-height: 30px;
                            min-height: 20px;
                        }
                    }
                }
            }
        }
    }
`
